import React from 'react';
import styles from './LayoutUsuario.module.css';
import {
  mdiAccountBox,
  mdiAccountEdit,
  mdiAccountKey,
  mdiClipboardTextClockOutline,
  mdiChartAreaspline,
  mdiFileChart,
  mdiInvoiceImport,
  mdiLan,
  mdiLoading,
  mdiMenu,
  mdiMonitor,
  mdiRouterNetwork,
  mdiRouterWireless,
  mdiSecurity,
  mdiServer,
  mdiStar,
  mdiSwapHorizontal,
  mdiSync,
  mdiVpn,
  mdiWan,
  mdiWifi,
  mdiNetwork,
  mdiShieldCheck,
  mdiDns,
  mdiSpeedometer,
  mdiChartBar,
  mdiLanConnect,
  mdiRobotOutline,
  mdiKeyChainVariant,
  mdiBellAlertOutline,
  mdiCogOutline,
} from '@mdi/js';
import MenuItem from './MenuItem';
import { useAuth } from '../../lib/useAuth';
import { MenuGroup } from './MenuGroup';
import Icon from '@mdi/react';
import Botao from '../Botao';
import { useAppliance } from '../../lib/useAppliance';
import { useSidebarBadges } from '../../lib/useSidebarBadges';
import { useAlertasResumo } from '../../lib/useAlertas';
import CampoSeletor from '../Formulario/CampoSeletor';
import axios from 'axios';
import { toastError } from '@/lib/toast';
import ThemeToggle from './ThemeToggle';
import AssistentePanel from '../ia/AssistentePanel';
import { useIaHabilitada } from '../../lib/useAssistente';
import { useSyncJob, phaseLabel } from '../../lib/syncJob';

type SidebarStatusTone = 'success' | 'warning' | 'danger' | 'info' | 'idle';

const SidebarCountBadge = ({
  value,
  title,
}: {
  value: string;
  title?: string;
}) => (
  <div title={title} className={[styles.sidebarBadge, styles.sidebarCountBadge].join(' ')}>
    <span className={styles.sidebarBadgeValue}>{value}</span>
  </div>
);

const SidebarBreakdownBadge = ({
  value,
  title,
  items,
}: {
  value: string;
  title?: string;
  items: Array<{ icon: string; value: number }>;
}) => (
  <div title={title} className={[styles.sidebarBadge, styles.sidebarCountBadge].join(' ')}>
    <span className={styles.sidebarBadgeValue}>{value}</span>
    {items.length > 0 ? <span className={styles.sidebarBadgeDivider} /> : null}
    {items.map((item) => (
      <span key={`${item.icon}-${item.value}`} className={styles.sidebarBadgeMetric}>
        <Icon path={item.icon} size="12px" />
        <span>{item.value}</span>
      </span>
    ))}
  </div>
);

const SidebarStatusBadge = ({
  label,
  title,
  tone,
}: {
  label: string;
  title?: string;
  tone: SidebarStatusTone;
}) => (
  <div
    title={title}
    className={[
      styles.sidebarBadge,
      styles.sidebarStatusBadge,
      styles[`sidebarStatus${tone[0].toUpperCase()}${tone.slice(1)}`],
    ].join(' ')}
  >
    <span className={styles.sidebarStatusDot} />
    <span>{label}</span>
  </div>
);

const ApplianceSidebarSelector = () => {
  const { applianceId, setApplianceId, applianceOptions, selectedAppliance, isLoading } = useAppliance();
  // Sync é long-running job: o POST volta na hora com o sessaoId; o hook
  // acompanha por polling e emite o toast do status final (useSyncJob).
  const sync = useSyncJob(applianceId);

  const handleSync = async () => {
    if (!applianceId || sync.rodando) return;
    if (!selectedAppliance?.agent_id) {
      toastError('Esta appliance não possui um agente configurado.');
      return;
    }
    await sync.disparar();
  };

  if (isLoading || applianceOptions.length === 0) return null;
  return (
    <div className={styles.applianceSelector}>
      <span className={styles.applianceSelectorLabel}>Appliance ativa</span>
      <CampoSeletor
        value={applianceId}
        onChange={setApplianceId}
        options={applianceOptions}
        placeholder="Selecione..."
        noSearch={applianceOptions.length <= 6}
        className={styles.applianceSelectorField}
      />
      <Botao
        variant="outline-success"
        size="sm"
        icon={sync.rodando ? mdiLoading : mdiSync}
        iconProps={{ spin: sync.rodando }}
        disabled={sync.rodando || !applianceId || !selectedAppliance?.agent_id}
        onClick={handleSync}
        style={{ marginTop: '6px', width: '100%' }}
      >
        {sync.rodando
          ? sync.progresso
            ? `${phaseLabel(sync.progresso.phase)}${sync.progresso.area_total ? ` (${(sync.progresso.area_index ?? 0) + 1}/${sync.progresso.area_total})` : ''}`
            : 'Sincronizando...'
          : 'Sincronizar'}
      </Botao>
    </div>
  );
};

const LayoutUsuario = ({ children }: { children: React.ReactNode }) => {
  const rolesAdmin = ['ADMIN'];
  const rolesManager = [...rolesAdmin, 'GESTOR'];
  const rolesUser = [...rolesManager, 'USUARIO'];

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [assistenteAberto, setAssistenteAberto] = React.useState(false);
  const { user } = useAuth();
  const sidebarBadges = useSidebarBadges();
  const alertasResumo = useAlertasResumo();
  // Antes do early return abaixo: hooks não podem ficar depois dele.
  const iaConfigurada = useIaHabilitada();

  if (!user) return null;

  // O backend também exige o papel; aqui é só para não mostrar um botão inútil.
  const iaHabilitada = iaConfigurada === true && rolesManager.includes(user.papel);

  // Problemas ativos: a cor segue o mais grave (vermelho > amarelo > azul).
  const alertasBadge = alertasResumo.total > 0 ? (
    <SidebarStatusBadge
      label={String(alertasResumo.total)}
      tone={alertasResumo.critico ? 'danger' : alertasResumo.aviso ? 'warning' : 'info'}
      title={`${alertasResumo.critico} crítico(s), ${alertasResumo.aviso} aviso(s), ${alertasResumo.info} informativo(s)`}
    />
  ) : undefined;

  const appliancesBadge = (
    <SidebarCountBadge
      value={String(sidebarBadges.appliancesCount)}
      title={`${sidebarBadges.appliancesCount} appliances cadastradas`}
    />
  );

  // Mesmo formato do badge de appliances, com o detalhe que muda decisão:
  // quantos estão online e quantos ainda esperam adoção.
  const agentesBadge = sidebarBadges.agentesCount != null
    ? (
      <SidebarCountBadge
        value={String(sidebarBadges.agentesCount)}
        title={[
          `${sidebarBadges.agentesCount} agentes`,
          `${sidebarBadges.agentesOnline ?? 0} online`,
          ...(sidebarBadges.agentesNaoAdotados ? [`${sidebarBadges.agentesNaoAdotados} aguardando adoção`] : []),
        ].join(', ')}
      />
    )
    : null;

  const firewallBadge = sidebarBadges.firewallPoliciesCount != null
    ? (
      <SidebarBreakdownBadge
        value={String(sidebarBadges.firewallPoliciesCount)}
        items={sidebarBadges.firewallPoliciesForSelectedAppliance != null
          ? [
            { icon: mdiRouterNetwork, value: sidebarBadges.firewallPoliciesForSelectedAppliance },
          ]
          : []}
        title={sidebarBadges.firewallPoliciesForSelectedAppliance != null
          ? `${sidebarBadges.firewallPoliciesCount} políticas de firewall cadastradas, ${sidebarBadges.firewallPoliciesForSelectedAppliance} na appliance selecionada`
          : `${sidebarBadges.firewallPoliciesCount} políticas de firewall cadastradas`}
      />
    )
    : null;

  const radiusBadge = sidebarBadges.radiusUsersCount != null
    ? (
      <SidebarBreakdownBadge
        value={String(sidebarBadges.radiusUsersCount)}
        items={sidebarBadges.radiusUsersForSelectedAppliance != null
          ? [
            { icon: mdiRouterNetwork, value: sidebarBadges.radiusUsersForSelectedAppliance },
          ]
          : []}
        title={sidebarBadges.radiusUsersForSelectedAppliance != null
          ? `${sidebarBadges.radiusUsersCount} usuários RADIUS cadastrados, ${sidebarBadges.radiusUsersForSelectedAppliance} na appliance selecionada`
          : `${sidebarBadges.radiusUsersCount} usuários RADIUS cadastrados`}
      />
    )
    : null;

  const natBadge = sidebarBadges.natCount != null
    ? (
      <SidebarCountBadge
        value={String(sidebarBadges.natCount)}
        title={`${sidebarBadges.natCount} redirecionamentos de porta`}
      />
    )
    : null;

  const vpnBadge = sidebarBadges.vpnsCount != null
    ? (
      <SidebarBreakdownBadge
        value={String(sidebarBadges.vpnsCount)}
        items={sidebarBadges.vpnsCount > 0
          ? [
            { icon: mdiMonitor, value: sidebarBadges.vpnClientsCount ?? 0 },
            { icon: mdiServer, value: sidebarBadges.vpnServersCount ?? 0 },
          ]
          : []}
        title={`${sidebarBadges.vpnsCount} VPNs, ${sidebarBadges.vpnClientsCount ?? 0} clientes e ${sidebarBadges.vpnServersCount ?? 0} servidores`}
      />
    )
    : null;

  const devicesBadge = sidebarBadges.devicesCount != null
    ? (
      <SidebarCountBadge
        value={String(sidebarBadges.devicesCount)}
        title={`${sidebarBadges.devicesCount} dispositivos cadastrados`}
      />
    )
    : null;

  const hotspotsBadge = sidebarBadges.hotspotsCount != null
    ? (
      <SidebarBreakdownBadge
        value={String(sidebarBadges.hotspotsCount)}
        items={sidebarBadges.hotspotsForSelectedAppliance != null
          ? [
            { icon: mdiRouterNetwork, value: sidebarBadges.hotspotsForSelectedAppliance },
          ]
          : []}
        title={sidebarBadges.hotspotsForSelectedAppliance != null
          ? `${sidebarBadges.hotspotsCount} hotspots cadastrados, ${sidebarBadges.hotspotsForSelectedAppliance} na appliance selecionada`
          : `${sidebarBadges.hotspotsCount} hotspots cadastrados`}
      />
    )
    : null;

  const networksBadge = sidebarBadges.networksCount != null
    ? (
      <SidebarBreakdownBadge
        value={String(sidebarBadges.networksCount)}
        items={sidebarBadges.networksCount > 0
          ? [
            { icon: mdiLan, value: sidebarBadges.lansCount ?? 0 },
            { icon: mdiWan, value: sidebarBadges.wansCount ?? 0 },
          ]
          : []}
        title={`${sidebarBadges.networksCount} redes, ${sidebarBadges.lansCount ?? 0} LANs e ${sidebarBadges.wansCount ?? 0} WANs`}
      />
    )
    : null;

  const redirectsBadge = sidebarBadges.redirectsCount != null
    ? (
      <SidebarCountBadge
        value={String(sidebarBadges.redirectsCount)}
        title={`${sidebarBadges.redirectsCount} redirecionamentos de tráfego`}
      />
    )
    : null;

  const squidBadge = sidebarBadges.squidState
    ? (
      <SidebarStatusBadge
        label={sidebarBadges.squidState === 'active'
          ? 'Ligado'
          : sidebarBadges.squidState === 'inactive'
            ? 'Desligado'
            : sidebarBadges.squidState === 'checking'
              ? 'Checando'
              : sidebarBadges.squidState === 'disabled'
                ? 'Desligado'
                : 'Erro'}
        tone={sidebarBadges.squidState === 'active'
          ? 'success'
          : sidebarBadges.squidState === 'checking'
            ? 'warning'
            : sidebarBadges.squidState === 'error' || sidebarBadges.squidState === 'inactive'
              ? 'danger'
              : 'idle'}
        title={sidebarBadges.squidState === 'active'
          ? 'Squid ativo'
          : sidebarBadges.squidState === 'checking'
            ? 'Squid aguardando verificação do agente'
            : sidebarBadges.squidState === 'error'
              ? 'Squid com erro de verificação'
              : 'Squid desligado'}
      />
    )
    : null;

  const dnsBadge = sidebarBadges.dnsCacheErrored
    ? <SidebarStatusBadge label="Erro" tone="danger" title="Erro ao consultar o status do cache DNS" />
    : sidebarBadges.dnsCacheEnabled != null
      ? (
        <SidebarStatusBadge
          label={sidebarBadges.dnsCacheEnabled ? 'Ligado' : 'Desligado'}
          tone={sidebarBadges.dnsCacheEnabled ? 'success' : 'idle'}
          title={sidebarBadges.dnsCacheEnabled ? 'Cache DNS ativo' : 'Cache DNS desligado'}
        />
      )
      : null;

  const qosBadge = sidebarBadges.qosErrored
    ? <SidebarStatusBadge label="Erro" tone="danger" title="Erro ao consultar o controle de banda" />
    : sidebarBadges.qosEnabled != null
      ? (
        <SidebarStatusBadge
          label={sidebarBadges.qosEnabled ? 'Ativo' : 'Desligado'}
          tone={sidebarBadges.qosEnabled ? 'success' : 'idle'}
          title={sidebarBadges.qosEnabled ? 'Controle de banda ativo' : 'Controle de banda desligado'}
        />
      )
      : null;

  return (
    <div className={styles.container}>
      <div
        className={[styles.sidebar, menuOpen ? styles.open : null].join(' ')}
      >
        <div className={[styles.logobar, 'font-webip'].join(' ')}>
          <Botao
            className={styles.btnMenu}
            variant="transparent"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <Icon path={mdiMenu} size={1.5} />
          </Botao>
          <img src="/logo.png" alt="Logo WebIP" style={{ height: '32px' }} />
          <span>
            <b>Web</b>IP
          </span>
          <div className={styles.logoSpacer} />
          {iaHabilitada && (
            <Botao
              className={styles.btnAssistente}
              variant="transparent"
              title="Gestão com IA"
              onClick={() => setAssistenteAberto((v) => !v)}
            >
              <Icon path={mdiRobotOutline} size={1.1} />
            </Botao>
          )}
          <ThemeToggle />
        </div>

        <MenuGroup>
          <MenuItem
            roles={rolesAdmin}
            label="Appliances"
            icon={mdiRouterNetwork}
            path="/appliances"
            badge={appliancesBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Agentes"
            icon={mdiRouterWireless}
            path="/agentes"
            badge={agentesBadge}
          />
          <MenuItem
            roles={rolesManager}
            label="Alertas"
            icon={mdiBellAlertOutline}
            path="/alertas"
            badge={alertasBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Firewall"
            icon={mdiSecurity}
            path="/firewall"
            badge={firewallBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Hotspot (Portal Cativo)"
            icon={mdiWifi}
            path="/hotspot"
            badge={hotspotsBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Usuários RADIUS"
            icon={mdiAccountKey}
            path="/radius"
            badge={radiusBadge}
          />
          <MenuItem
            roles={rolesManager}
            label="Relatórios"
            icon={mdiChartAreaspline}
            path="/relatorios"
          />
        </MenuGroup>

        <MenuGroup>
          <ApplianceSidebarSelector />

          <MenuItem
            roles={rolesAdmin}
            label="Redir. de Portas"
            icon={mdiSwapHorizontal}
            path="/nat"
            badge={natBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="VPNs"
            icon={mdiVpn}
            path="/vpn"
            badge={vpnBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Dispositivos"
            icon={mdiMonitor}
            path="/dispositivos"
            badge={devicesBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Redes"
            icon={mdiNetwork}
            path="/redes"
            badge={networksBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Redir. de tráfego"
            icon={mdiRouterNetwork}
            path="/redirects"
            badge={redirectsBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Squid (Proxy)"
            icon={mdiShieldCheck}
            path="/squid"
            badge={squidBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Cache DNS"
            icon={mdiDns}
            path="/dns-cache"
            badge={dnsBadge}
          />
          <MenuItem
            roles={rolesAdmin}
            label="Controle de banda"
            icon={mdiSpeedometer}
            path="/controle-banda"
            badge={qosBadge}
          />
          <MenuItem
            roles={rolesManager}
            label="Relatórios de Acesso"
            icon={mdiFileChart}
            path="/relatorios/acessos"
          />
          <MenuItem
            roles={rolesManager}
            label="Tráfego por link"
            icon={mdiChartBar}
            path="/trafego-links"
          />
          <MenuItem
            roles={rolesManager}
            label="Conexões ativas"
            icon={mdiLanConnect}
            path="/conexoes"
          />
        </MenuGroup>

        <MenuGroup>
          <MenuItem
            roles={rolesAdmin}
            label="Usuários"
            icon={mdiAccountBox}
            path="/usuario"
          />
          <MenuItem
            roles={rolesAdmin}
            label="Auditoria"
            icon={mdiClipboardTextClockOutline}
            path="/auditoria"
          />
          <MenuItem
            roles={rolesAdmin}
            label="Acesso MCP"
            icon={mdiKeyChainVariant}
            path="/mcp"
          />
          <MenuItem
            roles={rolesAdmin}
            label="Configurações gerais"
            icon={mdiCogOutline}
            path="/configuracoes"
          />
          {/* <MenuItem
              roles={rolesAdmin}
              label="Faturas"
              icon={mdiCurrencyBrl}
              path="/fatura"
            /> */}
        </MenuGroup>

        {/* <MenuItem disabled label="Avalie-nos!" icon={mdiStar} path="/" /> */}
        <MenuItem label={user?.nome} icon={mdiAccountEdit} path="/perfil" />
        <MenuItem label="Sair" icon={mdiInvoiceImport} path="/logout" />
      </div>
      <div className={styles.contents}>{children}</div>
      {iaHabilitada && (
        <AssistentePanel
          aberto={assistenteAberto}
          onFechar={() => setAssistenteAberto(false)}
        />
      )}
    </div>
  );
};

export default LayoutUsuario;
